import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import VisibilityIcon from '@mui/icons-material/Visibility';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import ArticleIcon from '@mui/icons-material/Article';
import DescriptionIcon from '@mui/icons-material/Description';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import PublicOffIcon from '@mui/icons-material/PublicOff';
import SpeakerNotesIcon from '@mui/icons-material/SpeakerNotes';
import SpeakerNotesOffIcon from '@mui/icons-material/SpeakerNotesOff';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../shared/components/PageHeader';
import { SelectGroupHeader } from '../../shared/components/SelectGroupHeader';
import { FormDialog } from '../../shared/components/Dialogs/FormDialog';
import { DocumentTypeDetailsDialog } from '../../shared/components/Dialogs/DocumentTypeDetailsDialog';
import { resources } from '../../core/services/resources';
import { api, extractErrorMessage } from '../../core/services/api';
import type { DocumentCategory, DocumentType, DocumentTypeSection } from '../../core/types/data-models';

// ─── helpers ────────────────────────────────────────────────────────────────

const IMPORTANCE_COLORS: Record<string, 'error' | 'warning' | 'default'> = {
  HIGH: 'error',
  MEDIUM: 'warning',
  LOW: 'default',
};

// ─── Category dialog ──────────────────────────────────────────────────────────

interface CategoryDialogProps {
  open: boolean;
  initial?: DocumentCategory | null;
  onClose: () => void;
}

function CategoryDialog({ open, initial, onClose }: CategoryDialogProps) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [typeTouched, setTypeTouched] = useState(false);
  const [description, setDescription] = useState('');
  const [order, setOrder] = useState<string>('0');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(initial?.name ?? '');
      setType(initial?.type ?? '');
      setTypeTouched(!!initial);
      setDescription(initial?.description ?? '');
      setOrder(initial?.order != null ? String(initial.order) : '0');
      setError(null);
    }
  }, [open]);

  // For new categories, keep type in sync with name until the user edits it directly.
  useEffect(() => {
    if (!initial && !typeTouched) {
      setType(name.trim().toUpperCase().replace(/\s+/g, '_'));
    }
  }, [name]);

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        name: name.trim(),
        type: type.trim().toUpperCase().replace(/\s+/g, '_'),
        description: description.trim() || null,
        order: order !== '' ? Number(order) : null,
      };
      if (initial) return resources.documentCategories.update(initial.id, payload);
      return resources.documentCategories.create(payload);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['documentCategories'] }); onClose(); },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  return (
    <FormDialog
      open={open}
      title={initial ? 'Edit Category' : 'New Category'}
      onClose={onClose}
      onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
      loading={save.isPending}
      maxWidth="sm"
      submitLabel={initial ? 'Save' : 'Create'}
    >
      <Box>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <Stack spacing={2}>
          <TextField label="Name" required value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          <TextField
            label="Type"
            required
            value={type}
            onChange={(e) => { setType(e.target.value); setTypeTouched(true); }}
            helperText="Unique identifier used by document types to reference this category. Changing it will not update document types already assigned to the previous value."
          />
          <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} multiline rows={3} />
          <TextField
            label="Order"
            type="number"
            value={order}
            onChange={(e) => setOrder(e.target.value)}
            helperText="Display order within the list (lower = first)"
            inputProps={{ min: 0 }}
          />
        </Stack>
      </Box>
    </FormDialog>
  );
}

// ─── helpers ─────────────────────────────────────────────────────────────────

function buildHierarchyList(
  types: DocumentType[],
  parentType: string | null = null,
  depth = 0,
): { docType: DocumentType; depth: number }[] {
  return types
    .filter((t) => t.parentDocumentType === parentType)
    .sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity))
    .flatMap((t) => [{ docType: t, depth }, ...buildHierarchyList(types, t.type, depth + 1)]);
}

/**
 * Flattens a category's document types into active-only, hierarchy (parent-first) order.
 * Walks the full tree (including inactive nodes) so active descendants of an inactive
 * parent are still reached, but only active types are included in the output.
 */
function collectActiveDocumentTypes(types: DocumentType[], parentType: string | null = null): DocumentType[] {
  return types
    .filter((t) => t.parentDocumentType === parentType)
    .sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity))
    .flatMap((t) => [
      ...(t.status === 'Active' ? [t] : []),
      ...collectActiveDocumentTypes(types, t.type),
    ]);
}

// ─── DocumentType dialog ──────────────────────────────────────────────────────

interface DocTypeDialogProps {
  open: boolean;
  initial?: DocumentType | null;
  categoryType: string;
  parentDocumentType?: string | null;
  categories: DocumentCategory[];
  allTypes: DocumentType[];
  allSections: DocumentTypeSection[];
  onClose: () => void;
}

function DocTypeDialog({ open, initial, categoryType, parentDocumentType = null, categories, allTypes, allSections, onClose }: DocTypeDialogProps) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [llmPrompt, setLlmPrompt] = useState('');
  const [catType, setCatType] = useState(categoryType);
  const [parentId, setParentId] = useState<string>('');
  const [dependentTypes, setDependentTypes] = useState<string[]>([]);
  const [relatedSections, setRelatedSections] = useState<string[]>([]);
  const [collapsedCats, setCollapsedCats] = useState<Set<string>>(new Set());
  const [collapsedParentCats, setCollapsedParentCats] = useState<Set<string>>(new Set());
  const [collapsedSectionTypes, setCollapsedSectionTypes] = useState<Set<string>>(new Set());
  const [importance, setImportance] = useState<string>('MEDIUM');
  const [order, setOrder] = useState<string>('0');
  const [status, setStatus] = useState<string>('Active');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName(initial?.name ?? '');
      setLabel(initial?.label ?? '');
      setDescription(initial?.description ?? '');
      setLlmPrompt(initial?.llmPrompt ?? '');
      setCatType(initial?.categoryType ?? categoryType);
      setParentId(initial?.parentDocumentType ?? parentDocumentType ?? '');
      setDependentTypes(initial?.dependentDocumentTypes ?? []);
      setRelatedSections(initial?.relatedSections ?? []);
      setCollapsedCats(new Set());
      setCollapsedParentCats(new Set());
      setCollapsedSectionTypes(new Set());
      setImportance(initial?.importance ?? 'MEDIUM');
      setOrder(initial?.order != null ? String(initial.order) : '0');
      setStatus(initial?.status ?? 'Active');
      setError(null);
    }
  }, [open]);

  // `type` is derived from the label and is what sections, child types and
  // relatedSections entries reference by string — a blank label yields a blank
  // identifier and silently detaches all of them. `required` on the fields
  // below is cosmetic, because FormDialog renders its form with `noValidate`.
  const normalizedType = label.trim().toUpperCase().replace(/\s+/g, '_');
  const labelError = normalizedType === '' ? 'Label is required' : null;

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        name: name.trim(), label: label.trim(),
        type: normalizedType as any,
        description: description.trim() || null,
        llmPrompt: llmPrompt.trim() || null,
        categoryType: catType,
        parentDocumentType: parentId === '' ? null : parentId,
        dependentDocumentTypes: dependentTypes,
        relatedSections: relatedSections,
        importance: importance as DocumentType['importance'],
        order: order !== '' ? Number(order) : null,
        status: status as DocumentType['status'],
      };
      if (initial) return resources.documentTypes.update(initial.id, payload);
      return resources.documentTypes.create(payload);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['documentTypes'] }); onClose(); },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  return (
    <FormDialog
      open={open}
      title={initial ? 'Edit Document Type' : 'New Document Type'}
      onClose={onClose}
      onSubmit={(e) => { e.preventDefault(); if (labelError) { setError(labelError); return; } save.mutate(); }}
      loading={save.isPending}
      maxWidth="md"
      submitLabel={initial ? 'Save' : 'Create'}
    >
      <Box>
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        <Stack spacing={2}>
          <TextField label="Name" required value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          <TextField label="Label (unique identifier)" required value={label} onChange={(e) => setLabel(e.target.value)} helperText="Short uppercase slug, e.g. PRD, BRD, ARCH_SPEC" />
          <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)} multiline rows={3} />
          <TextField
            label="LLM Prompt"
            value={llmPrompt}
            onChange={(e) => setLlmPrompt(e.target.value)}
            multiline
            rows={6}
            helperText="Markdown / Jinja2 template used by the LLM agent when generating this document type"
          />
          <Stack direction="row" spacing={2}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Parent document type (optional)</InputLabel>
              <Select
                value={parentId}
                onChange={(e) => setParentId(e.target.value)}
                input={<OutlinedInput label="Parent document type (optional)" />}
                renderValue={(val) =>
                  val === '' ? '— none —' : (allTypes.find((t) => t.type === val)?.name ?? '—')
                }
                displayEmpty
                MenuProps={{ PaperProps: { style: { maxHeight: 360 } } }}
              >
                <MenuItem value="">— none —</MenuItem>
                {(() => {
                  const hierarchyItems = buildHierarchyList(allTypes.filter((t) => t.id !== initial?.id));
                  return categories.flatMap((cat) => {
                    const catItems = hierarchyItems.filter(({ docType }) => docType.categoryType === cat.type);
                    if (catItems.length === 0) return [];
                    const isCollapsed = collapsedParentCats.has(cat.type);
                    return [
                      <SelectGroupHeader
                        key={`cat-${cat.type}`}
                        label={cat.name}
                        collapsed={isCollapsed}
                        onToggle={() => setCollapsedParentCats((prev) => {
                          const next = new Set(prev);
                          if (next.has(cat.type)) next.delete(cat.type);
                          else next.add(cat.type);
                          return next;
                        })}
                      />,
                      ...(!isCollapsed ? catItems.map(({ docType: t, depth }) => (
                        <MenuItem key={t.id} value={t.type} sx={{ pl: 1 + depth * 2 }}>
                          <ListItemText primary={t.name} />
                        </MenuItem>
                      )) : []),
                    ];
                  });
                })()}
              </Select>
            </FormControl>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Dependent document types</InputLabel>
              <Select
                multiple
                value={dependentTypes}
                onChange={(e) => setDependentTypes(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value as string[])}
                input={<OutlinedInput label="Dependent document types" />}
                renderValue={(selected) =>
                  (selected as string[])
                    .map((v) => allTypes.find((t) => t.type === v)?.name ?? v)
                    .join(', ')
                }
                MenuProps={{ PaperProps: { style: { maxHeight: 360 } } }}
              >
                {(() => {
                  const hierarchyItems = buildHierarchyList(allTypes.filter((t) => t.id !== initial?.id));
                  return categories.flatMap((cat) => {
                    const catItems = hierarchyItems.filter(({ docType }) => docType.categoryType === cat.type);
                    if (catItems.length === 0) return [];
                    const isCollapsed = collapsedCats.has(cat.type);
                    return [
                      <SelectGroupHeader
                        key={`cat-${cat.type}`}
                        label={cat.name}
                        collapsed={isCollapsed}
                        onToggle={() => setCollapsedCats((prev) => {
                          const next = new Set(prev);
                          if (next.has(cat.type)) next.delete(cat.type);
                          else next.add(cat.type);
                          return next;
                        })}
                      />,
                      ...(!isCollapsed ? catItems.map(({ docType: t, depth }) => (
                        <MenuItem key={t.id} value={t.type} sx={{ pl: 1 + depth * 2 }}>
                          <Checkbox checked={dependentTypes.includes(t.type)} size="small" />
                          <ListItemText primary={t.name} />
                        </MenuItem>
                      )) : []),
                    ];
                  });
                })()}
              </Select>
            </FormControl>
          </Stack>
          <FormControl fullWidth>
            <InputLabel>Related sections</InputLabel>
            <Select
              multiple
              value={relatedSections}
              onChange={(e) => setRelatedSections(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value as string[])}
              input={<OutlinedInput label="Related sections" />}
              renderValue={(selected) =>
                (selected as string[])
                  .map((v) => allSections.find((s) => s.type === v)?.title ?? v)
                  .join(', ')
              }
              MenuProps={{ PaperProps: { style: { maxHeight: 360 } } }}
            >
              {allTypes.flatMap((t) => {
                const typeSections = allSections
                  .filter((s) => s.documentType === t.type)
                  .sort((a, b) => (a.defaultOrder ?? Infinity) - (b.defaultOrder ?? Infinity));
                if (typeSections.length === 0) return [];
                const isCollapsed = collapsedSectionTypes.has(t.type);
                return [
                  <SelectGroupHeader
                    key={`sect-group-${t.type}`}
                    label={t.name}
                    collapsed={isCollapsed}
                    onToggle={() => setCollapsedSectionTypes((prev) => {
                      const next = new Set(prev);
                      if (next.has(t.type)) next.delete(t.type);
                      else next.add(t.type);
                      return next;
                    })}
                  />,
                  ...(!isCollapsed ? typeSections.map((s) => (
                    <MenuItem key={s.type} value={s.type} sx={{ pl: 3 }}>
                      <Checkbox checked={relatedSections.includes(s.type)} size="small" />
                      <ListItemText primary={s.title} />
                    </MenuItem>
                  )) : []),
                ];
              })}
            </Select>
          </FormControl>
          <Stack direction="row" spacing={2}>
            <TextField select label="Category" required value={catType} onChange={(e) => setCatType(e.target.value)} sx={{ flex: 1 }}>
              {categories.map((c) => <MenuItem key={c.type} value={c.type}>{c.name}</MenuItem>)}
            </TextField>
            <TextField select label="Importance" value={importance} onChange={(e) => setImportance(e.target.value)} sx={{ flex: 1 }}>
              {(['HIGH', 'MEDIUM', 'LOW'] as const).map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
            </TextField>
          </Stack>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Order"
              type="number"
              value={order}
              onChange={(e) => setOrder(e.target.value)}
              helperText="Display order within the category (lower = first)"
              inputProps={{ min: 0 }}
              sx={{ flex: 1 }}
            />
            <TextField select label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ flex: 1 }}>
              {(['Active', 'Disabled'] as const).map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
            </TextField>
          </Stack>
        </Stack>
      </Box>
    </FormDialog>
  );
}

// ─── TypeNode ─────────────────────────────────────────────────────────────────

interface TypeNodeProps {
  type: DocumentType;
  allTypes: DocumentType[];
  allCategories: DocumentCategory[];
  allSections: DocumentTypeSection[];
  depth: number;
  /** When non-null, a global expand/collapse was requested — sync into local state. */
  forceOpen: boolean | null;
  onAddType: (categoryType: string, parentType: string | null) => void;
  onEditType: (t: DocumentType) => void;
  onViewType: (t: DocumentType) => void;
  onShowDetails: (t: DocumentType) => void;
}

function TypeNode({ type, allTypes, allCategories, allSections, depth, forceOpen, onAddType, onEditType, onViewType, onShowDetails }: TypeNodeProps) {
  // Local state drives open/close; forceOpen syncs into it when changed globally.
  const [open, setOpen] = useState(true);

  useEffect(() => {
    if (forceOpen !== null) setOpen(forceOpen);
  }, [forceOpen]);

  const children = allTypes
    .filter((t) => t.parentDocumentType === type.type)
    .sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity));
  const hasChildren = children.length > 0;

  const typeSections = allSections
    .filter((s) => s.documentType === type.type)
    .sort((a, b) => (a.defaultOrder ?? Infinity) - (b.defaultOrder ?? Infinity));

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{
          pl: 2 + depth * 3,
          pr: 1,
          py: 0.5,
          borderRadius: 1,
          '&:hover': { bgcolor: 'action.hover' },
          '&:hover .row-actions': { opacity: 1 },
        }}
      >
        {/* expand/collapse toggle — always toggles local state */}
        <IconButton
          size="small"
          onClick={() => setOpen((v) => !v)}
          sx={{ visibility: hasChildren ? 'visible' : 'hidden', p: 0.25 }}
        >
          {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
        </IconButton>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ width: 20, textAlign: 'right', flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}
        >
          {type.order ?? '-'}
        </Typography>

        <ArticleIcon fontSize="small" sx={{ color: 'text.secondary', flexShrink: 0 }} />

        {/* Name + description + icons — takes remaining space */}
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={500} noWrap sx={{ flexShrink: 0 }}>
            {type.name}
          </Typography>
          {type.description && (
            <Tooltip
              title={type.description}
              placement="top"
              arrow
              slotProps={{ tooltip: { sx: { fontSize: 13, maxWidth: 480 } } }}
            >
              <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.disabled', cursor: 'default', flexShrink: 0 }} />
            </Tooltip>
          )}
          {typeSections.length > 0 && (
            <Tooltip
              placement="bottom-start"
              arrow
              slotProps={{
                tooltip: {
                  sx: {
                    fontSize: 13,
                    maxWidth: 380,
                    p: 0,
                    maxHeight: 360,
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': { width: 4 },
                    '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.35)', borderRadius: 2 },
                  },
                },
                popper: {
                  modifiers: [
                    { name: 'preventOverflow', enabled: false },
                    { name: 'flip', options: { fallbackPlacements: ['top-start'] } },
                  ],
                },
              }}
              title={
                <Box sx={{ py: 0.75, px: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 11, display: 'block', mb: 0.5, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Sections ({typeSections.length})
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2, listStyleType: 'disc' }}>
                    {typeSections.map((s) => (
                      <li key={s.id}>{s.title}</li>
                    ))}
                  </Box>
                </Box>
              }
            >
              <FormatListBulletedIcon fontSize="small" sx={{ color: 'text.disabled', cursor: 'default', flexShrink: 0 }} />
            </Tooltip>
          )}
          {type.dependentDocumentTypes?.length > 0 && (
            <Tooltip
              placement="bottom-start"
              arrow
              slotProps={{
                tooltip: {
                  sx: {
                    fontSize: 13,
                    maxWidth: 380,
                    p: 0,
                    maxHeight: 360,
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': { width: 4 },
                    '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(255,255,255,0.35)', borderRadius: 2 },
                  },
                },
                popper: {
                  modifiers: [
                    { name: 'preventOverflow', enabled: false },
                    { name: 'flip', options: { fallbackPlacements: ['top-start'] } },
                  ],
                },
              }}
              title={
                <Box sx={{ py: 0.75, px: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: 11, display: 'block', mb: 0.5, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Depends on ({type.dependentDocumentTypes.length})
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2, listStyleType: 'disc' }}>
                    {type.dependentDocumentTypes.map((depType) => {
                      const depName = allTypes.find((t) => t.type === depType)?.name ?? depType;
                      return <li key={depType}>{depName}</li>;
                    })}
                  </Box>
                </Box>
              }
            >
              <AccountTreeIcon fontSize="small" sx={{ color: 'text.disabled', cursor: 'default', flexShrink: 0 }} />
            </Tooltip>
          )}
          <Tooltip title={type.useWebSearch ? 'Web search enabled' : 'Web search disabled'}>
            {type.useWebSearch
              ? <TravelExploreIcon fontSize="small" sx={{ color: 'info.main', cursor: 'default', flexShrink: 0 }} />
              : <PublicOffIcon fontSize="small" sx={{ color: 'text.disabled', cursor: 'default', flexShrink: 0 }} />}
          </Tooltip>
          <Tooltip title={type.useProjectPrompt ? 'Project prompt enabled' : 'Project prompt disabled'}>
            {type.useProjectPrompt
              ? <SpeakerNotesIcon fontSize="small" sx={{ color: 'info.main', cursor: 'default', flexShrink: 0 }} />
              : <SpeakerNotesOffIcon fontSize="small" sx={{ color: 'text.disabled', cursor: 'default', flexShrink: 0 }} />}
          </Tooltip>
        </Stack>

        <Chip
          label={type.importance}
          size="small"
          color={IMPORTANCE_COLORS[type.importance] ?? 'default'}
          sx={{ height: 18, fontSize: 10 }}
        />
        <Chip
          label={type.status}
          size="small"
          variant="outlined"
          color={type.status === 'Disabled' ? 'default' : 'success'}
          sx={{ mr: 1, height: 18, fontSize: 10 }}
        />

        <Stack direction="row" className="row-actions" sx={{ opacity: 0, transition: 'opacity 0.15s' }}>
          <Tooltip title="View document type details">
            <IconButton size="small" onClick={() => onViewType(type)}><VisibilityIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="View LLM prompt & sections (Markdown / JSON)">
            <IconButton size="small" onClick={() => onShowDetails(type)}><DescriptionIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Edit document type">
            <IconButton size="small" onClick={() => onEditType(type)}><EditIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Add sub document type">
            <IconButton size="small" onClick={() => onAddType(type.categoryType, type.type)}><AddIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {hasChildren && (
        <Collapse in={open}>
          {children.map((child) => (
            <TypeNode
              key={child.id}
              type={child}
              allTypes={allTypes}
              allCategories={allCategories}
              allSections={allSections}
              depth={depth + 1}
              forceOpen={forceOpen}
              onAddType={onAddType}
              onEditType={onEditType}
              onViewType={onViewType}
              onShowDetails={onShowDetails}
            />
          ))}
        </Collapse>
      )}
    </Box>
  );
}

// ─── CategoryRow ──────────────────────────────────────────────────────────────

interface CategoryRowProps {
  category: DocumentCategory;
  types: DocumentType[];
  allTypes: DocumentType[];
  allCategories: DocumentCategory[];
  allSections: DocumentTypeSection[];
  forceOpen: boolean | null;
  onEditCategory: (c: DocumentCategory) => void;
  onAddType: (categoryType: string, parentType: string | null) => void;
  onEditType: (t: DocumentType) => void;
  onViewType: (t: DocumentType) => void;
  onShowDetails: (t: DocumentType) => void;
  onShowCategoryDetails: (c: DocumentCategory) => void;
}

function CategoryRow({ category, types, allTypes, allCategories, allSections, forceOpen, onEditCategory, onAddType, onEditType, onViewType, onShowDetails, onShowCategoryDetails }: CategoryRowProps) {
  // Default collapsed (false); syncs with global forceOpen.
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (forceOpen !== null) setOpen(forceOpen);
  }, [forceOpen]);

  const rootTypes = types
    .filter((t) => t.parentDocumentType === null)
    .sort((a, b) => (a.order ?? Infinity) - (b.order ?? Infinity));

  return (
    <Box sx={{ mb: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
      {/* Category header — click anywhere to toggle */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          px: 2, py: 1,
          bgcolor: 'action.hover',
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover .row-actions': { opacity: 1 },
        }}
        onClick={() => setOpen((v) => !v)}
      >
        <IconButton size="small" sx={{ p: 0.25 }} onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}>
          {open ? <ExpandMoreIcon /> : <ChevronRightIcon />}
        </IconButton>
        {open
          ? <FolderOpenIcon fontSize="small" color="primary" />
          : <FolderIcon fontSize="small" color="primary" />}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, flexShrink: 0 }}>
          {category.name}
        </Typography>
        {category.description && (
          <Typography variant="caption" color="text.secondary"
            sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {category.description}
          </Typography>
        )}
        <Stack direction="row" className="row-actions" sx={{ opacity: 0, transition: 'opacity 0.15s' }}
          onClick={(e) => e.stopPropagation()}>
          <Tooltip title="View LLM prompts & sections for all active documents">
            <IconButton size="small" onClick={() => onShowCategoryDetails(category)}><DescriptionIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Edit category">
            <IconButton size="small" onClick={() => onEditCategory(category)}><EditIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Tooltip title="Add document type">
            <IconButton size="small" onClick={() => onAddType(category.type, null)}><AddIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Document type tree */}
      <Collapse in={open}>
        <Box sx={{ py: 0.5 }}>
          {rootTypes.length === 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ pl: 4, display: 'block', py: 1 }}>
              No document types yet.
            </Typography>
          ) : (
            rootTypes.map((t) => (
              <TypeNode
                key={t.id}
                type={t}
                allTypes={allTypes}
                allCategories={allCategories}
                allSections={allSections}
                depth={0}
                forceOpen={forceOpen}
                onAddType={onAddType}
                onEditType={onEditType}
                onViewType={onViewType}
                onShowDetails={onShowDetails}
              />
            ))
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

// ─── Dependency Tree Dialog ─────────────────────────────────────────────────────

const DT_NODE_W = 180;
const DT_NODE_H = 52;
const DT_H_GAP = 90;
const DT_V_GAP = 14;
const DT_PADDING = 28;

const IMPORTANCE_ACCENT: Record<string, string> = {
  HIGH: '#d32f2f',
  MEDIUM: '#ed6c02',
  LOW: '#9e9e9e',
};

function buildDepLayout(types: DocumentType[]) {
  const typeMap = new Map(types.map(t => [t.type, t]));
  const levels = new Map<string, number>();

  function computeLevel(key: string, path: Set<string> = new Set()): number {
    if (levels.has(key)) return levels.get(key)!;
    if (path.has(key)) return 0; // cycle guard
    const t = typeMap.get(key);
    if (!t || !t.dependentDocumentTypes?.length) {
      levels.set(key, 0);
      return 0;
    }
    const nextPath = new Set([...path, key]);
    const maxDep = Math.max(...t.dependentDocumentTypes.map(d => computeLevel(d, nextPath)));
    const level = maxDep + 1;
    levels.set(key, level);
    return level;
  }

  types.forEach(t => computeLevel(t.type));

  const byLevel: string[][] = [];
  levels.forEach((level, key) => {
    while (byLevel.length <= level) byLevel.push([]);
    byLevel[level].push(key);
  });
  byLevel.forEach(arr =>
    arr.sort((a, b) => {
      const ta = typeMap.get(a);
      const tb = typeMap.get(b);
      if (!ta || !tb) return 0;
      if (ta.categoryType !== tb.categoryType) return ta.categoryType.localeCompare(tb.categoryType);
      return (ta.order ?? 999) - (tb.order ?? 999);
    })
  );

  const maxCount = Math.max(...byLevel.map(a => a.length), 1);
  const totalH = maxCount * (DT_NODE_H + DT_V_GAP) - DT_V_GAP + DT_PADDING * 2;

  const positions = new Map<string, { x: number; y: number }>();
  byLevel.forEach((col, level) => {
    const colH = col.length * (DT_NODE_H + DT_V_GAP) - DT_V_GAP;
    const startY = (totalH - colH) / 2;
    col.forEach((key, idx) => {
      positions.set(key, {
        x: DT_PADDING + level * (DT_NODE_W + DT_H_GAP),
        y: startY + idx * (DT_NODE_H + DT_V_GAP),
      });
    });
  });

  const svgW = Math.max(DT_PADDING * 2 + byLevel.length * (DT_NODE_W + DT_H_GAP) - DT_H_GAP, 400);
  const svgH = Math.max(totalH, 200);

  return { positions, svgW, svgH };
}

interface DepTreeDialogProps {
  open: boolean;
  allTypes: DocumentType[];
  onClose: () => void;
}

function DependencyTreeDialog({ open, allTypes, onClose }: DepTreeDialogProps) {
  const { positions, svgW, svgH } = useMemo(() => buildDepLayout(allTypes), [allTypes]);
  const [selectedType, setSelectedType] = useState<string | null>(null);

  useEffect(() => { if (!open) setSelectedType(null); }, [open]);

  const highlightedSet = useMemo<Set<string>>(() => {
    if (!selectedType) return new Set();
    const typeMap = new Map(allTypes.map(t => [t.type, t]));
    const result = new Set<string>([selectedType]);

    // Direct dependencies (one level left)
    for (const dep of typeMap.get(selectedType)?.dependentDocumentTypes ?? []) {
      result.add(dep);
    }
    // Direct dependents (one level right)
    for (const t of allTypes) {
      if ((t.dependentDocumentTypes ?? []).includes(selectedType)) {
        result.add(t.type);
      }
    }
    return result;
  }, [selectedType, allTypes]);

  const nodeOpacity = (key: string) => !selectedType || highlightedSet.has(key) ? 1 : 0.12;
  const edgeHighlighted = (depKey: string, typeKey: string) =>
    !!selectedType && highlightedSet.has(depKey) && highlightedSet.has(typeKey);
  const edgeOpacity = (depKey: string, typeKey: string) =>
    !selectedType ? 1 : (edgeHighlighted(depKey, typeKey) ? 1 : 0.08);

  const handleNodeClick = (e: React.MouseEvent, key: string) => {
    e.stopPropagation();
    setSelectedType(prev => prev === key ? null : key);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth PaperProps={{ sx: { height: '90vh', display: 'flex', flexDirection: 'column' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AccountTreeIcon color="primary" />
          <Typography variant="h6">Document Dependency Tree</Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="caption" color="text.secondary">
            {selectedType
              ? 'Click the same node to deselect · Click another to switch'
              : 'Click a node to highlight its connections'}
            &nbsp;·&nbsp; Left bar:{' '}
            <Box component="span" sx={{ color: '#d32f2f', fontWeight: 600 }}>HIGH</Box>
            {' / '}
            <Box component="span" sx={{ color: '#ed6c02', fontWeight: 600 }}>MEDIUM</Box>
            {' / '}
            <Box component="span" sx={{ color: '#9e9e9e', fontWeight: 600 }}>LOW</Box>
          </Typography>
          <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, overflow: 'auto', flex: '1 1 0', minHeight: 0 }}>
          <svg
            width={svgW}
            height={svgH}
            style={{ display: 'block', background: '#f8f9fa', cursor: 'default' }}
            onClick={() => setSelectedType(null)}
          >
            <defs>
              <marker id="dt-arrow" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                <polygon points="0 0, 10 3.5, 0 7" fill="#bbb" />
              </marker>
              <marker id="dt-arrow-hl" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                <polygon points="0 0, 10 3.5, 0 7" fill="#1f6feb" />
              </marker>
            </defs>

            {/* Edges */}
            {allTypes.flatMap(t =>
              (t.dependentDocumentTypes ?? []).map(depKey => {
                const fromPos = positions.get(t.type);
                const toPos = positions.get(depKey);
                if (!fromPos || !toPos) return null;
                const x1 = toPos.x + DT_NODE_W;
                const y1 = toPos.y + DT_NODE_H / 2;
                const x2 = fromPos.x;
                const y2 = fromPos.y + DT_NODE_H / 2;
                const cx = (x1 + x2) / 2;
                const hl = edgeHighlighted(depKey, t.type);
                return (
                  <path
                    key={`${depKey}->${t.type}`}
                    d={`M ${x1} ${y1} C ${cx} ${y1}, ${cx} ${y2}, ${x2} ${y2}`}
                    fill="none"
                    stroke={hl ? '#1f6feb' : '#bbb'}
                    strokeWidth={hl ? 2 : 1.5}
                    opacity={edgeOpacity(depKey, t.type)}
                    markerEnd={hl ? 'url(#dt-arrow-hl)' : 'url(#dt-arrow)'}
                  />
                );
              })
            )}

            {/* Nodes */}
            {allTypes.map(t => {
              const pos = positions.get(t.type);
              if (!pos) return null;
              const accent = IMPORTANCE_ACCENT[t.importance] ?? '#9e9e9e';
              const clipId = `dt-clip-${t.type.replace(/[^a-zA-Z0-9]/g, '_')}`;
              const label = t.name.length > 22 ? `${t.name.slice(0, 20)}\u2026` : t.name;
              const isSelected = selectedType === t.type;
              const nodeFill = isSelected ? '#e8f1fd' : '#fff';
              const nodeStroke = isSelected ? '#1f6feb' : '#e3e8ef';
              const nodeStrokeW = isSelected ? 2.5 : 1.5;
              return (
                <g
                  key={t.type}
                  transform={`translate(${pos.x}, ${pos.y})`}
                  opacity={nodeOpacity(t.type)}
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => handleNodeClick(e, t.type)}
                >
                  <defs>
                    <clipPath id={clipId}>
                      <rect width={DT_NODE_W} height={DT_NODE_H} rx={6} ry={6} />
                    </clipPath>
                  </defs>
                  {/* Drop shadow */}
                  <rect x={2} y={3} width={DT_NODE_W} height={DT_NODE_H} rx={6} ry={6} fill="rgba(0,0,0,0.07)" />
                  {/* Node body */}
                  <rect width={DT_NODE_W} height={DT_NODE_H} rx={6} ry={6} fill={nodeFill} stroke={nodeStroke} strokeWidth={nodeStrokeW} />
                  {/* Left accent bar (clipped to node shape) */}
                  <rect clipPath={`url(#${clipId})`} x={0} y={0} width={6} height={DT_NODE_H} fill={accent} />
                  {/* Type name */}
                  <text
                    x={DT_NODE_W / 2 + 3}
                    y={DT_NODE_H / 2 - 6}
                    textAnchor="middle"
                    fill="#1a1a2e"
                    fontSize={12}
                    fontWeight="600"
                    fontFamily="Inter, sans-serif"
                  >
                    {label}
                  </text>
                  {/* Category */}
                  <text
                    x={DT_NODE_W / 2 + 3}
                    y={DT_NODE_H / 2 + 9}
                    textAnchor="middle"
                    fill="#888"
                    fontSize={10}
                    fontFamily="Inter, sans-serif"
                  >
                    {t.categoryType}
                  </text>
                </g>
              );
            })}
          </svg>
      </DialogContent>
    </Dialog>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function DocumentManagementPage() {
  const navigate = useNavigate();
  const { data: catEnv, isLoading: loadingCats, error: catError } = useQuery({
    queryKey: ['documentCategories'],
    queryFn: () => resources.documentCategories.list({ page: 1, pageSize: 200, sort: 'order' }),
  });
  const { data: typeEnv, isLoading: loadingTypes } = useQuery({
    queryKey: ['documentTypes'],
    queryFn: () => resources.documentTypes.list({ page: 1, pageSize: 500, sort: 'order' }),
  });
  const { data: sectionsEnv } = useQuery({
    queryKey: ['documentTypeSections'],
    queryFn: () => resources.documentTypeSections.list({ page: 1, pageSize: 2000, sort: 'defaultOrder' }),
  });

  const categories: DocumentCategory[] = (catEnv?.data ?? []).slice().sort(
    (a, b) => (a.order ?? Infinity) - (b.order ?? Infinity),
  );
  const allTypes: DocumentType[] = (typeEnv?.data ?? []).slice().sort(
    (a, b) => (a.order ?? Infinity) - (b.order ?? Infinity),
  );
  const allSections: DocumentTypeSection[] = sectionsEnv?.data ?? [];

  /**
   * null  = no global override (each node keeps its own state)
   * true  = all expanded
   * false = all collapsed
   *
   * We reset to null after a tick so subsequent individual toggles aren't
   * blocked by a stale forced value.
   */
  const [forceOpen, setForceOpen] = useState<boolean | null>(null);
  // "expanded" means last global action was expand; default is collapsed so false/null → expand icon
  const [lastForced, setLastForced] = useState<boolean>(false);

  const triggerForce = (value: boolean) => {
    setLastForced(value);
    setForceOpen(value);
    // Reset to null after nodes have had a chance to sync
    setTimeout(() => setForceOpen(null), 50);
  };

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [depTreeOpen, setDepTreeOpen] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const res = await api.get<Blob>('/document-categories/export-md', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'document-categories.md';
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(extractErrorMessage(err));
    } finally {
      setExporting(false);
    }
  };

  const [catDialog, setCatDialog] = useState<{ open: boolean; editing: DocumentCategory | null }>({ open: false, editing: null });
  const [typeDialog, setTypeDialog] = useState<{ open: boolean; editing: DocumentType | null; categoryType: string; parentId: string | null }>
    ({ open: false, editing: null, categoryType: '', parentId: null });

  const openNewCategory = () => setCatDialog({ open: true, editing: null });
  const openEditCategory = (c: DocumentCategory) => setCatDialog({ open: true, editing: c });
  const openAddType = (cType: string, parentType: string | null) => setTypeDialog({ open: true, editing: null, categoryType: cType, parentId: parentType });
  const openEditType = (t: DocumentType) => setTypeDialog({ open: true, editing: t, categoryType: t.categoryType, parentId: t.parentDocumentType });
  const openViewType = (t: DocumentType) => navigate(`/global-settings/document-types/${t.id}`);

  const [detailsDialog, setDetailsDialog] = useState<{ open: boolean; type: DocumentType | null }>({ open: false, type: null });
  const openDetails = (t: DocumentType) => setDetailsDialog({ open: true, type: t });
  const closeDetails = () => setDetailsDialog((prev) => ({ ...prev, open: false }));

  const [categoryDetailsDialog, setCategoryDetailsDialog] = useState<{ open: boolean; category: DocumentCategory | null }>({ open: false, category: null });
  const openCategoryDetails = (c: DocumentCategory) => setCategoryDetailsDialog({ open: true, category: c });
  const closeCategoryDetails = () => setCategoryDetailsDialog((prev) => ({ ...prev, open: false }));

  const [allDetailsOpen, setAllDetailsOpen] = useState(false);

  if (loadingCats || loadingTypes) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }
  if (catError) {
    return <Alert severity="error">{extractErrorMessage(catError)}</Alert>;
  }

  return (
    <Box>
      <PageHeader
        title="Document Management"
        description="Manage global document categories and document types."
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title={lastForced ? 'Collapse all' : 'Expand all'}>
              <IconButton
                size="small"
                onClick={() => triggerForce(!lastForced)}
                sx={{ border: '1px solid', borderColor: 'divider' }}
              >
                {lastForced ? <UnfoldLessIcon fontSize="small" /> : <UnfoldMoreIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<AccountTreeIcon />}
              onClick={() => setDepTreeOpen(true)}
            >
              Dependency Tree
            </Button>
            <Button
              variant="outlined"
              startIcon={<LibraryBooksIcon />}
              onClick={() => setAllDetailsOpen(true)}
            >
              View All
            </Button>
            <Button
              variant="outlined"
              startIcon={<FileDownloadIcon />}
              onClick={handleExport}
              disabled={exporting}
            >
              Export
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openNewCategory}>
              New Category
            </Button>
          </Stack>
        }
      />

      {exportError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExportError(null)}>{exportError}</Alert>}

      {categories.length === 0 ? (
        <Alert severity="info">No document categories yet. Click "New Category" to get started.</Alert>
      ) : (
        categories.map((cat) => (
          <CategoryRow
            key={cat.id}
            category={cat}
            types={allTypes.filter((t) => t.categoryType === cat.type)}
            allTypes={allTypes}
            allCategories={categories}
            allSections={allSections}
            forceOpen={forceOpen}
            onEditCategory={openEditCategory}
            onAddType={openAddType}
            onEditType={openEditType}
            onViewType={openViewType}
            onShowDetails={openDetails}
            onShowCategoryDetails={openCategoryDetails}
          />
        ))
      )}

      <CategoryDialog
        open={catDialog.open}
        initial={catDialog.editing}
        onClose={() => setCatDialog({ open: false, editing: null })}
      />
      <DocTypeDialog
        open={typeDialog.open}
        initial={typeDialog.editing}
        categoryType={typeDialog.categoryType}
        parentDocumentType={typeDialog.parentId}
        categories={categories}
        allTypes={allTypes}
        allSections={allSections}
        onClose={() => setTypeDialog({ open: false, editing: null, categoryType: '', parentId: null })}
      />
      <DependencyTreeDialog
        open={depTreeOpen}
        allTypes={allTypes.filter(t => t.status === 'Active')}
        onClose={() => setDepTreeOpen(false)}
      />
      {detailsDialog.type && (
        <DocumentTypeDetailsDialog
          open={detailsDialog.open}
          title={detailsDialog.type.name}
          documents={[{
            docType: detailsDialog.type,
            sections: allSections.filter((s) => s.documentType === detailsDialog.type!.type),
          }]}
          onClose={closeDetails}
        />
      )}
      {categoryDetailsDialog.category && (
        <DocumentTypeDetailsDialog
          open={categoryDetailsDialog.open}
          title={categoryDetailsDialog.category.name}
          documents={collectActiveDocumentTypes(
            allTypes.filter((t) => t.categoryType === categoryDetailsDialog.category!.type),
          ).map((docType) => ({
            docType,
            sections: allSections.filter((s) => s.documentType === docType.type),
          }))}
          onClose={closeCategoryDetails}
        />
      )}
      <DocumentTypeDetailsDialog
        open={allDetailsOpen}
        title="All Categories"
        categoryGroups={categories.map((category) => ({
          category,
          documents: collectActiveDocumentTypes(
            allTypes.filter((t) => t.categoryType === category.type),
          ).map((docType) => ({
            docType,
            sections: allSections.filter((s) => s.documentType === docType.type),
          })),
        }))}
        onClose={() => setAllDetailsOpen(false)}
      />
    </Box>
  );
}
